import React from 'react';
import { SEOHead } from './SEOHead';
import { StructuredData } from './StructuredData';
import { TrackingScripts } from './TrackingScripts';
import { SEO_CONFIG, PAGE_ROUTE_MAP } from '../../Setup/Config/seo-config';

/**
 * SEO - Parent component that combines SEOHead, StructuredData, and TrackingScripts
 * Standard way to add complete SEO setup to any page
 * 
 * @param {string} page - Page name (e.g., "home", "web-analyser") or route path (e.g., "/", "/web-analyser")
 * @param {string} title - Page title (optional, overrides config)
 * @param {string} description - Page description (optional, overrides config)
 * @param {string} keywords - Page keywords (optional, overrides config)
 * @param {string} ogImage - OG image URL (optional, overrides config)
 * @param {string} canonical - Canonical URL (optional, overrides config)
 * @param {string} type - OG type (default: 'website')
 * @param {string} url - Page URL for structured data (optional)
 * 
 * @example
 * // Simple usage with page name
 * <SEO page="home" />
 * <SEO page="web-analyser" />
 * 
 * @example
 * // Usage with route path
 * <SEO page="/" />
 * <SEO page="/web-analyser" />
 * 
 * @example
 * // Override specific fields
 * <SEO page="home" title="Custom Title" />
 * 
 * @example
 * // Dynamic data (no page prop)
 * <SEO title="Dynamic Title" description="Dynamic Description" ogImage="..." />
 */
export const SEO = ({
    page,
    title,
    description,
    keywords,
    ogImage,
    canonical,
    type,
    url
}) => {
    // Get config based on page name or route
    let pageConfig = null;

    if (page) {
        // Check if page is a route path (starts with /)
        if (page.startsWith('/')) {
            // Try to find page name from route
            const pageName = Object.keys(PAGE_ROUTE_MAP).find(
                key => PAGE_ROUTE_MAP[key] === page
            );
            pageConfig = pageName ? SEO_CONFIG[pageName] : null;
        } else {
            // Direct page name
            pageConfig = SEO_CONFIG[page];
        }
    }

    // Use config values if props not provided
    // Priority: props > pageConfig > null (but we need at least fallback)
    const finalTitle = title || pageConfig?.title || SEO_CONFIG.default.title;
    const finalDescription = description || pageConfig?.description || SEO_CONFIG.default.description;
    const finalKeywords = keywords || pageConfig?.keywords || SEO_CONFIG.default.keywords;
    const finalOgImage = ogImage || pageConfig?.ogImage || SEO_CONFIG.default.ogImage;
    const finalCanonical = canonical || pageConfig?.canonical || SEO_CONFIG.default.canonical;
    const finalType = type || pageConfig?.ogType || 'website';
    const finalUrl = url || finalCanonical;

    return (
        <>
            <SEOHead
                title={finalTitle}
                description={finalDescription}
                keywords={finalKeywords}
                ogImage={finalOgImage}
                canonical={finalCanonical}
                type={finalType}
            />
            <StructuredData
                title={finalTitle}
                description={finalDescription}
                url={finalUrl}
            />
            <TrackingScripts />
        </>
    );
};

